import { NotificationAction } from "@config/local-notifications/notification-actions";
import { NotificationIcon } from "@config/local-notifications/notification-icon";
import { organizationManagerNotificationRepo } from "@repositories";
import { copyApiPrefilterPerRole } from "@shared/backend/entity-security/copy-api-prefilter-per-role";
import { doesExist } from "@shared/backend/entity-security/does-exist";
import type { HyperEntityConfig } from "@shared/backend/hyper-entity.decorator";
import { awaiter } from "@shared/helpers/awaiter";
import { textBuilder } from "@shared/helpers/text-builder";
import { Roles } from "@shared/Roles";
import { Allow, remult } from "remult";
import { Association } from "../association/entity";
import { Team } from "../team";
import { type AssociationTeam } from "./entity";

export const AssociationTeamEntityConfig: HyperEntityConfig<AssociationTeam> = {
  allowApiRead: Allow.authenticated,
  allowApiCrud: [Roles.Admin, Roles.SuperAdmin, Roles.Manager, Roles.User],
  apiPrefilterPerRole: async (filter) => {
    filter(Roles.All, {
      $or: [
        {
          association: {
            $is: await copyApiPrefilterPerRole(Association),
          },
        },
        {
          team: {
            $is: await copyApiPrefilterPerRole(Team),
          },
        },
      ],
    });
  },

  allowInsertPerRole(allow) {
    allow(
      Roles.User,
      (entity) => !!remult.user?.associations?.includes(entity.associationId),
    );

    allow(Roles.OrganizationManagers, (entity) =>
      doesExist(Association, {
        id: entity.associationId,
        organizationId: remult.user?.organizationId!,
      }),
    );
  },

  allowDeleteAndUpdatePerRole(allow) {
    allow(
      Roles.User,
      (entity) => !!remult.user?.associations!.includes(entity.associationId),
    );

    allow(Roles.OrganizationManagers, {
      association: {
        $is: {
          organizationId: remult.user!.organizationId,
        },
      },
    });
  },
  async onInsert(entity, e) {
    const [association, team] = await awaiter(
      e.relations.association.findOne({
        include: {
          user: true,
        },
      }),
      e.relations.team.findOne({
        include: {
          managers: true,
        },
      }),
    );
    if (!association || !association.user || !team || !team.managers) return;

    const user = association.user;

    const text = textBuilder()
      .if(user.gender === "MALE", "המתנדב")
      .if(user.gender === "FEMALE", "המתנדבת")
      .if(user.gender === "OTHER", "המתנדב/ת")
      .space()
      .add(user.name)
      .space()
      .if(user.gender === "MALE", "נרשם")
      .if(user.gender === "FEMALE", "נרשמה")
      .if(user.gender === "OTHER", "נרשמ/ה")
      .space()
      .add("לפרויקט ")
      .add(team.name)
      .add(".")
      .done();

    await organizationManagerNotificationRepo.insert(
      team.managers.map((manager) => ({
        toId: manager.managerId,
        title: "מתנדב חדש",
        content: text,
        icon: NotificationIcon.NEW_USER_IN_GROUP,
        actions: [
          {
            btnText: "לפרטים",
            id: NotificationAction.NAVIGATE,
            extra: {
              to: `/volunteers/${association.id}`,
            },
          },
        ],
      })),
    );
  },
  id: {
    teamId: true,
    associationId: true,
  },
};
